// Phase 12 — Agency Portal RBAC capability matrix.
//
// Capabilities are intentionally fine-grained (action-scoped) so that pages can
// gate individual buttons / mutations independently of route-level access. The
// role table is canonical; see the Phase 0 plan for the source of truth.
//
// NOTE: scope (which shops/brands the employee can act on) is enforced at the
// use-site via `useAccessibleShops`. The matrix below answers ONLY the question
// "is this capability *ever* available to this role?"

export type EmployeeRole =
  | 'owner'
  | 'team_lead'
  | 'manager'
  | 'analyst'
  | 'read_only';

export type Capability =
  | 'clients.view'
  | 'clients.create'
  | 'clients.delete'
  // `clients.manage` is the umbrella write capability for the /clients page
  // (add / edit / delete brand). Mirrors the role set of clients.create +
  // clients.delete (owner/manager) — kept as a distinct key so callers can
  // gate the whole brand-management surface without enumerating sub-actions.
  | 'clients.manage'
  | 'clients.connect_platform'
  | 'proposals.view'
  | 'proposals.create'
  // `proposals.propose` is the canonical name from the W1 role matrix; it is
  // an alias of the older `proposals.create` and carries the identical role
  // set. Both are kept so existing call-sites keep compiling.
  | 'proposals.propose'
  | 'proposals.approve'
  | 'proposals.deny'
  | 'team.view'
  | 'team.invite'
  | 'team.delete'
  | 'skills.view'
  | 'skills.install'
  | 'skills.create_org'
  | 'skills.delete'
  | 'billing.view'
  | 'billing.manage'
  // `integrations.view` gates READ of the Integrations Hub — the platform grid
  // and the connected MCP-server list. Held by EVERY role: seeing which
  // platforms a brand is connected to is the point of a read-only seat. Mirrors
  // the backend `require_role:can:integrations.view` gate on the read routes.
  | 'integrations.view'
  // `integrations.manage` gates connect/manage of MCP + platform integrations
  // (owner/team_lead/manager). Same role set as `clients.connect_platform`,
  // exposed under the matrix's canonical name.
  | 'integrations.manage'
  // Phase 4 — Tool Access surface (per-tool, per-employee MCP enablement).
  // `mcp.access.view` gates the nav entry + read access to the grid
  // (owner/manager/team_lead). `mcp.access.manage` gates every mutation
  // (toggle / bulk / preset / alias edit) — owner/manager only; team_lead is
  // intentionally view-only. analyst/read_only get neither (no nav entry).
  | 'mcp.access.view'
  | 'mcp.access.manage'
  | 'notifications.view'
  | 'notifications.configure'
  | 'stats.view'
  // Per-user stats / audit trail — owner & manager only (matrix: "view
  // per-user stats / audit"). Distinct from `stats.view` (granted to all
  // roles for aggregate data).
  | 'stats.perUser'
  | 'audit.view'
  // Per-agency Meta API activity & rate-limit view — owner & manager only
  // (same gate set as `audit.view`). Distinct key so the page/sidebar can be
  // toggled independently of the proposal audit trail.
  | 'meta_activity.view'
  // Per-agency MCP tool-call activity view — owner & manager only (same gate
  // set as `meta_activity.view`). Drives the Log Center "MCP Calls" tab; every
  // MCP tool call (reads + writes, official + custom client MCPs) the org made.
  | 'mcp_calls.view'
  | 'settings.manage_org'
  // ---------------------------------------------------------------------
  // Client-report portal (P1–P3). NOTE: the `clients.*` group above means
  // BRANDS; this group is deliberately namespaced `portal.*` to avoid the
  // collision (see plan §5 "Locked decisions" / backend
  // Modules/Identity/Support/EmployeeCapabilities.php). These keys MIRROR the
  // backend matrix EXACTLY — do not drift them.
  //   portal.reports.review  — review/edit report drafts   (analyst+)
  //   portal.reports.approve — approve/reject/req-changes   (team_lead+)
  //   portal.branding.manage — report branding + presets    (manager+)
  //   portal.kpis.manage     — per-brand KPI/targets         (team_lead+)
  //   portal.clients.manage  — invite/list/revoke clients    (team_lead+)
  //   portal.domains.manage  — agency portal domains         (owner/manager)
  //   portal.email.manage    — agency email/Resend settings  (owner/manager)
  //   portal.settings.manage — per-brand portal settings     (team_lead+)
  | 'portal.reports.review'
  | 'portal.reports.approve'
  | 'portal.branding.manage'
  | 'portal.kpis.manage'
  | 'portal.clients.manage'
  | 'portal.domains.manage'
  | 'portal.email.manage'
  | 'portal.settings.manage';

// All capabilities — used to derive the owner row so we never drift.
const ALL_CAPABILITIES: Capability[] = [
  'clients.view',
  'clients.create',
  'clients.delete',
  'clients.manage',
  'clients.connect_platform',
  'proposals.view',
  'proposals.create',
  'proposals.propose',
  'proposals.approve',
  'proposals.deny',
  'team.view',
  'team.invite',
  'team.delete',
  'skills.view',
  'skills.install',
  'skills.create_org',
  'skills.delete',
  'billing.view',
  'billing.manage',
  'integrations.view',
  'integrations.manage',
  'mcp.access.view',
  'mcp.access.manage',
  'notifications.view',
  'notifications.configure',
  'stats.view',
  'stats.perUser',
  'audit.view',
  'meta_activity.view',
  'mcp_calls.view',
  'settings.manage_org',
  'portal.reports.review',
  'portal.reports.approve',
  'portal.branding.manage',
  'portal.kpis.manage',
  'portal.clients.manage',
  'portal.domains.manage',
  'portal.email.manage',
  'portal.settings.manage',
];

// Capability matrix — keep aligned with the Phase 0 plan role table.
export const ROLE_CAPABILITIES: Record<EmployeeRole, Capability[]> = {
  // Owner: full access. Seller-only logins are auto-bridged as owners (Phase 11).
  owner: ALL_CAPABILITIES,

  // Manager: the SENIOR delivery role (hierarchy: owner > manager > team_lead >
  // analyst > read_only). Scoped to their group's brands. Everything except
  // deleting clients and managing org-wide settings. Scope checked at use-site.
  manager: [
    'clients.view',
    'clients.create',
    'clients.manage',
    'clients.connect_platform',
    'integrations.view',
    'integrations.manage',
    'mcp.access.view',
    'mcp.access.manage',
    'proposals.view',
    'proposals.create',
    'proposals.propose',
    'proposals.approve',
    'proposals.deny',
    'team.view',
    'team.invite',
    'team.delete',
    'skills.view',
    'skills.install',
    'skills.create_org',
    'skills.delete',
    'billing.view',
    'notifications.view',
    'notifications.configure',
    'stats.view',
    'stats.perUser',
    'audit.view',
    'meta_activity.view',
    'mcp_calls.view',
    // Client-report portal: manager+ holds the full portal group.
    'portal.reports.review',
    'portal.reports.approve',
    'portal.branding.manage',
    'portal.kpis.manage',
    'portal.clients.manage',
    'portal.domains.manage',
    'portal.email.manage',
    'portal.settings.manage',
  ],

  // Team lead: the JUNIOR delivery role (below manager). Assigned shops only.
  // Can propose AND approve (within threshold).
  team_lead: [
    'clients.view',
    'clients.connect_platform',
    'integrations.view',
    'integrations.manage',
    // Phase 4 — team_lead can SEE the Tool Access grid but cannot mutate it
    // (no `mcp.access.manage`). The page renders read-only for this role.
    'mcp.access.view',
    'proposals.view',
    'proposals.create',
    'proposals.propose',
    'proposals.approve',
    'proposals.deny',
    'skills.view',
    'billing.view',
    'notifications.view',
    'notifications.configure',
    'stats.view',
    // Client-report portal: team_lead+ reviews AND approves reports, manages
    // KPIs / portal clients / per-brand settings (NOT branding/domains/email).
    'portal.reports.review',
    'portal.reports.approve',
    'portal.kpis.manage',
    'portal.clients.manage',
    'portal.settings.manage',
  ],

  // Analyst: assigned shops only. Can propose, cannot approve.
  analyst: [
    'clients.view',
    'integrations.view',
    'proposals.view',
    'proposals.create',
    'proposals.propose',
    'skills.view',
    'stats.view',
    // Client-report portal: analyst+ can review/edit drafts but NOT approve.
    'portal.reports.review',
  ],

  // Read-only: view everything they're scoped to, mutate nothing.
  read_only: [
    'clients.view',
    'integrations.view',
    'proposals.view',
    'team.view',
    'skills.view',
    'billing.view',
    'notifications.view',
    'stats.view',
  ],
};

/**
 * True when `role` is permitted to perform `cap`. Null/undefined roles are
 * always denied (treat as unauthenticated). Unknown roles also deny.
 *
 * NOTE (Phase 1 — custom roles): this is the *fallback* resolver, keyed on the
 * static role→capability matrix above. It is retained for transition safety and
 * for the seller-only bridge (owners who never load a `/me` capability list).
 * Prefer the live-capability path: the `useCan` hook and `<RoleGate>` consult
 * the acting employee's effective `capabilities` from Redux when present and
 * only drop back to this matrix when that list is absent. `canResolve()` below
 * is the single source of truth both paths share.
 */
export function can(
  role: EmployeeRole | null | undefined,
  cap: Capability
): boolean {
  if (!role) return false;
  return ROLE_CAPABILITIES[role]?.includes(cap) ?? false;
}

/**
 * Live-aware capability resolver. This is what the React hooks/components use.
 *
 * Resolution order:
 *   1. If a live `capabilities` array is present (the flat `/me.capabilities`
 *      list), it is authoritative — return whether it contains `cap`. This is
 *      what makes owner role-edits take effect immediately: the matrix is no
 *      longer consulted once the backend has spoken.
 *   2. Otherwise fall back to the static `ROLE_CAPABILITIES[role]` matrix
 *      (transition safety / seller-only owner bridge / pre-`/me` first paint).
 *
 * `capabilities` is treated as "present" only when it is a real array — an
 * `undefined`/`null` value means the backend didn't send it, so we fall back.
 * An explicitly empty array `[]` is a valid (fully-locked-down) answer and does
 * NOT fall through to the matrix.
 */
export function canResolve(
  capabilities: string[] | null | undefined,
  role: EmployeeRole | null | undefined,
  cap: Capability
): boolean {
  if (Array.isArray(capabilities)) {
    return capabilities.includes(cap);
  }
  return can(role, cap);
}
